// STRATUL 2D AL INSTALAȚIILOR, pe planul de nivel: traseele nivelurilor
// desenate, în culoarea sistemului și la lățimea lor reală (în metri, ca
// restul planului), punctat ce trece prin șapă; coloanele ca cercuri,
// echipamentele ca dreptunghiuri. Stă în același `<g>` cu căile de evacuare
// (`plan/EvacuareTrasee`), deci în cadrul planului, fără nicio transformare.

import type { MouseEvent } from "react";
import { useInstalatii } from "./context";
import { CULOARE, SISTEME, type Segment } from "./model";

/** Lățimea minimă desenată: o țeavă DN 16 ar fi o linie de 2 cm. */
const LATIME_MIN_M = 0.035;
const CULOARE_ALES = "#ffc107";

const vertical = (s: Segment) => s.puncte.length === 2
  && Math.hypot(s.puncte[0][0] - s.puncte[1][0], s.puncte[0][1] - s.puncte[1][1]) < 1e-6;

export default function StratPlanInstalatii({ niveluri, cotaJos, cotaSus }: {
  /** `StoreyPlan.id`-urile desenate acum. */
  niveluri: ReadonlySet<string>;
  /** Fereastra de cote a nivelului, ca să se vadă doar coloanele care trec prin el. */
  cotaJos: number;
  cotaSus: number;
}) {
  const inst = useInstalatii();
  const m = inst?.model;
  if (!inst || !m) return null;
  const alege = (id: string) => (e: MouseEvent) => { e.stopPropagation(); inst.alege(id); };
  const ordine = SISTEME.map((s) => s.sistem).filter((s) => inst.vizibile.has(s));
  return (
    <g className="inst-plan">
      {ordine.map((sistem) => (
        <g key={sistem}>
          {m.segmente.filter((s) => s.sistem === sistem && !vertical(s) && s.storey && niveluri.has(s.storey))
            .map((s) => {
              const ales = inst.ales === s.id;
              return (
                <polyline key={s.id} points={s.puncte.map((p) => `${p[0]},${p[1]}`).join(" ")}
                          fill="none" stroke={ales ? CULOARE_ALES : CULOARE[s.sistem]}
                          strokeWidth={Math.max(s.sectiune?.l ?? s.d_m, LATIME_MIN_M) * (ales ? 1.6 : 1)}
                          strokeDasharray={s.mediu === "sapa" ? "0.15 0.1" : undefined}
                          strokeLinejoin="round" strokeLinecap="round" opacity={0.9}
                          style={{ cursor: "pointer" }} onClick={alege(s.id)} />
              );
            })}
          {m.segmente.filter((s) => s.sistem === sistem && vertical(s)
            && Math.min(s.puncte[0][2], s.puncte[1][2]) <= cotaSus
            && Math.max(s.puncte[0][2], s.puncte[1][2]) >= cotaJos)
            .map((s) => (
              <circle key={s.id} cx={s.puncte[0][0]} cy={s.puncte[0][1]}
                      r={Math.max(s.d_m, LATIME_MIN_M * 2) / 2}
                      fill={inst.ales === s.id ? CULOARE_ALES : CULOARE[s.sistem]}
                      stroke="#fff" strokeWidth={0.015} style={{ cursor: "pointer" }} onClick={alege(s.id)} />
            ))}
          {m.echipamente.filter((e) => e.sistem === sistem && e.storey && niveluri.has(e.storey))
            .map((e) => (
              <rect key={e.id} x={-e.dim[0] / 2} y={-e.dim[1] / 2} width={e.dim[0]} height={e.dim[1]}
                    transform={`translate(${e.centru[0]},${e.centru[1]}) rotate(${e.deg ?? 0})`}
                    fill={e.fel === "ghena_propusa" ? "none" : "#fff"}
                    stroke={inst.ales === e.id ? CULOARE_ALES : CULOARE[e.sistem]}
                    strokeWidth={0.03} strokeDasharray={e.fel === "ghena_propusa" ? "0.08 0.05" : undefined}
                    style={{ cursor: "pointer" }} onClick={alege(e.id)} />
            ))}
        </g>
      ))}
    </g>
  );
}
